import type {
  Org,
  Period,
  StoreRow,
  StoreType,
  SummaryResponse,
  StoresResponse,
  RegionsResponse,
  RegionRow,
  StoreDetailResponse,
  CommonParams,
  StoresParams,
  RegionsParams,
} from './types';

const CR = 1e7;
const cr = (n: number) => Math.round(n * CR);
const pct = (s: number, ly: number): number | null => (ly > 0 ? Math.round(((s - ly) / ly) * 1000) / 10 : null);

const PERIOD_SCALE: Record<Period, number> = { today: 1, yesterday: 0.92, wtd: 5.6, mtd: 19.5 };
const PERIOD_YOY_SHIFT: Record<Period, number> = { today: 1, yesterday: 0.97, wtd: 1.03, mtd: 0.99 };

// [site_code, name, type, region, city, state, salesCr, lySalesCr]
type Seed = [string, string, StoreType, string, string, string, number, number];
const SU_SEEDS: Seed[] = [
  ['HYD001', 'Hyd-Nexus One', 'PTL', 'SOUTH', 'Hyderabad', 'Telangana', 1.78, 2.5],
  ['BLR002', 'Blr-Forum Mall', 'PTL', 'SOUTH', 'Bengaluru', 'Karnataka', 1.64, 2.12],
  ['HYD003', 'Hyd-Inorbit Mall', 'PTL', 'SOUTH', 'Hyderabad', 'Telangana', 1.52, 1.98],
  ['CHN004', 'Chn-Phoenix Market City', 'PTL', 'SOUTH', 'Chennai', 'Tamil Nadu', 1.47, 1.71],
  ['KOC005', 'Koc-Lulu Mall', 'PTL', 'SOUTH', 'Kochi', 'Kerala', 1.39, 1.22],
  ['VZG006', 'Vzg-CMR Central', 'LTL', 'SOUTH', 'Visakhapatnam', 'Andhra Pradesh', 1.21, 1.43],
  ['BLR007', 'Blr-Orion Mall', 'PTL', 'SOUTH', 'Bengaluru', 'Karnataka', 1.18, 1.65],
  ['MYS008', 'Mys-Mall of Mysore', 'LTL', 'SOUTH', 'Mysuru', 'Karnataka', 0.97, 1.05],
  ['WGL009', 'Wgl-Warangal Central', 'LTL', 'SOUTH', 'Warangal', 'Telangana', 0.84, 0.88],
  ['PUN010', 'Pun-Amanora Mall', 'PTL', 'WEST', 'Pune', 'Maharashtra', 0.79, 1.12],
  ['MNG011', 'Mng-City Centre', 'LTL', 'SOUTH', 'Mangaluru', 'Karnataka', 0.66, 0.59],
  ['BBS012', 'Bbs-Esplanade', 'LTL', 'EAST', 'Bhubaneswar', 'Odisha', 0.52, 0.71],
  ['GOA013', 'Goa-Panjim Central', 'LTL', 'WEST', 'Panaji', 'Goa', 0.41, 0.44],
  ['BLR014', 'Blr-Whitefield New', 'NEW_STORE', 'SOUTH', 'Bengaluru', 'Karnataka', 0.38, 0],
  ['HYD015', 'Hyd-Kokapet New', 'NEW_STORE', 'SOUTH', 'Hyderabad', 'Telangana', 0.29, 0],
];


const ETH_SEEDS: Seed[] = [
  ['ETH101', 'Eth-Inorbit', 'PTL', 'WEST', 'Mumbai', 'Maharashtra', 2.31, 2.05],
  ['ETH102', 'Eth-Forum', 'PTL', 'SOUTH', 'Bengaluru', 'Karnataka', 1.96, 2.2],
  ['ETH103', 'Eth-Palladium', 'PTL', 'WEST', 'Ahmedabad', 'Gujarat', 1.71, 1.52],
  ['ETH104', 'Eth-Select Citywalk', 'PTL', 'NORTH', 'New Delhi', 'Delhi', 1.58, 1.8],
  ['ETH105', 'Eth-DLF Avenue', 'LTL', 'NORTH', 'Gurugram', 'Haryana', 1.23, 1.1],
  ['ETH106', 'Eth-Elante', 'LTL', 'NORTH', 'Chandigarh', 'Chandigarh', 0.94, 1.02],
  ['ETH107', 'Eth-Phoenix Pune', 'PTL', 'WEST', 'Pune', 'Maharashtra', 0.88, 0.8],
  ['ETH108', 'Eth-Mall of India', 'LTL', 'NORTH', 'Noida', 'Uttar Pradesh', 0.72, 0.9],
  ['ETH109', 'Eth-Lulu Hyderabad', 'NEW_STORE', 'SOUTH', 'Hyderabad', 'Telangana', 0.35, 0],
  ['ETH110', 'Eth-Ambience', 'NEW_STORE', 'NORTH', 'Gurugram', 'Haryana', 0.27, 0],
];

interface Dataset {
  seeds: Seed[];
  states: Array<[string, number, number, number]>;
  baseSalesCr: number;
  baseLyCr: number;
  storeCount: number;
  lflStoreCount: number;
}

const fullRows = (org: Org): StoreRow[] =>
  DATA[org].seeds.map(([site_code, store_name, store_type, region_code, city, , s, ly]) => ({
    rank: 0,
    site_code,
    store_name,
    store_type,
    region_code,
    city,
    sales: cr(s),
    ly_sales: cr(ly),
    yoy_pct: pct(s, ly),
  }));

// state, store_count, salesCr, lyCr
const SU_STATES: Dataset['states'] = [
  ['Karnataka', 39, 42.1, 55.3],
  ['Telangana', 37, 38.4, 49.9],
  ['Andhra Pradesh', 30, 28.7, 36.2],
  ['Kerala', 25, 24.9, 22.8],
  ['Tamil Nadu', 24, 21.6, 27.4],
  ['Maharashtra', 14, 11.3, 15.6],
  ['Odisha', 8, 6.4, 8.1],
  ['Goa', 4, 3.1, 3.3],
  ['Others', 6, 5.21, 16.07],
];
const ETH_STATES: Dataset['states'] = [
  ['Maharashtra', 18, 31.2, 28.4],
  ['Karnataka', 11, 17.9, 20.1],
  ['Delhi', 9, 14.6, 16.3],
  ['Gujarat', 8, 12.1, 10.7],
  ['Haryana', 7, 10.4, 9.6],
  ['Uttar Pradesh', 6, 7.8, 9.1],
  ['Others', 5, 3.4, 4.1],
];

const DATA: Record<Org, Dataset> = {
  SU: { seeds: SU_SEEDS, states: SU_STATES, baseSalesCr: 181.71, baseLyCr: 234.67, storeCount: 187, lflStoreCount: 164 },
  ETH: { seeds: ETH_SEEDS, states: ETH_STATES, baseSalesCr: 97.4, baseLyCr: 99.1, storeCount: 64, lflStoreCount: 52 },
};

function scaleRow(r: StoreRow, p: Period): StoreRow {
  const k = PERIOD_SCALE[p];
  const sales = Math.round(r.sales * k);
  const ly_sales = Math.round(r.ly_sales * k * PERIOD_YOY_SHIFT[p]);
  return { ...r, sales, ly_sales, yoy_pct: pct(sales, ly_sales) };
}

const splitList = (s?: string) => (s ? s.split(',').map((x) => x.trim()).filter(Boolean) : []);

function filteredRows(params: CommonParams, org: Org): StoreRow[] {
  const types = splitList(params.store_type);
  const regions = splitList(params.region);
  const cities = splitList(params.city);
  return fullRows(org)
    .filter((r) => (types.length ? types.includes(r.store_type) : true))
    .filter((r) => (regions.length ? regions.includes(r.region_code) : true))
    .filter((r) => (cities.length ? cities.includes(r.city) : true))
    .map((r) => scaleRow(r, params.period));
}

const rank = (rows: StoreRow[]): StoreRow[] => rows.map((r, i) => ({ ...r, rank: i + 1 }));

export function mockSummary(params: CommonParams, org: Org = 'SU'): SummaryResponse {
  const k = PERIOD_SCALE[params.period];
  const ds = DATA[org];
  const allSeedSales = ds.seeds.reduce((a, s) => a + s[6], 0);
  const rows = filteredRows(params, org);
  const filteredSales = rows.reduce((a, r) => a + r.sales, 0) / k / CR;
  const share = Math.min(1, filteredSales / allSeedSales) || 0;

  const baseSales = cr(ds.baseSalesCr) * k * share;
  const baseLy = cr(ds.baseLyCr) * k * PERIOD_YOY_SHIFT[params.period] * share;
  const sales = Math.round(baseSales);
  const ly_sales = Math.round(baseLy);
  const sorted = [...rows].sort((a, b) => b.sales - a.sales);
  const lflSales = Math.round(sales * 0.78);
  const lflLy = Math.round(ly_sales * 0.7);
  return {
    period: params.period,
    as_of: '2026-10-20T23:59:00+05:30',
    network: { sales, ly_sales, yoy_pct: pct(sales, ly_sales), store_count: Math.round(ds.storeCount * share) },
    lfl: { sales: lflSales, ly_sales: lflLy, yoy_pct: pct(lflSales, lflLy), store_count: Math.round(ds.lflStoreCount * share) },
    top_stores: rank(sorted.slice(0, 5)),
    bottom_stores: rank([...sorted].reverse().slice(0, 5)),
  };
}

export function mockStores(params: StoresParams, org: Org = 'SU'): StoresResponse {
  const key = params.sort === 'yoy' ? 'yoy_pct' : 'sales';
  const dir = params.order === 'asc' ? 1 : -1;
  const rows = filteredRows(params, org).sort((a, b) => {
    const av = a[key] ?? -Infinity;
    const bv = b[key] ?? -Infinity;
    return (av - bv) * dir;
  });
  const limit = params.limit ?? 50;
  const offset = params.offset ?? 0;
  const items = rank(rows).slice(offset, offset + limit);
  return { period: params.period, total: rows.length, limit, offset, items };
}


export function mockRegions(params: RegionsParams, org: Org = 'SU'): RegionsResponse {
  const k = PERIOD_SCALE[params.period];
  const groupBy = params.group_by ?? 'state';
  const ds = DATA[org];
  let items: RegionRow[];
  if (groupBy === 'state') {
    items = ds.states.map(([state, store_count, s, ly]) => {
      const sales = cr(s * k);
      const ly_sales = cr(ly * k * PERIOD_YOY_SHIFT[params.period]);
      return { rank: 0, key: state, state, store_count, sales, ly_sales, yoy_pct: pct(sales, ly_sales) };
    });
  } else {
    const byCity = new Map<string, { state: string; n: number; s: number; ly: number }>();
    ds.seeds.forEach(([, , , , city, state, s, ly]) => {
      const e = byCity.get(city) ?? { state, n: 0, s: 0, ly: 0 };
      e.n += 1;
      e.s += s;
      e.ly += ly;
      byCity.set(city, e);
    });
    items = Array.from(byCity.entries()).map(([city, e]) => {
      const sales = cr(e.s * k);
      const ly_sales = cr(e.ly * k * PERIOD_YOY_SHIFT[params.period]);
      return { rank: 0, key: city, state: e.state, store_count: e.n, sales, ly_sales, yoy_pct: pct(sales, ly_sales) };
    });
  }
  if (params.region) items = items.filter((r) => r.state === params.region);
  items = items.sort((a, b) => b.sales - a.sales).map((r, i) => ({ ...r, rank: i + 1 }));
  return { period: params.period, group_by: groupBy, total_stores: ds.storeCount, items };
}

export function mockStoreDetail(siteCode: string, org: Org = 'SU'): StoreDetailResponse {
  const found = DATA[org].seeds.find((s) => s[0] === siteCode);
  if (!found) {
    const err = new Error('Request failed with status code 404') as Error & {
      response?: { status: number; data: unknown };
      isAxiosError?: boolean;
    };
    err.isAxiosError = true;
    err.response = { status: 404, data: { detail: 'Store not found' } };
    throw err;
  }
  const seed = found;
  const [code, name, type, , city, state, s, ly] = seed;
  const base: StoreRow = {
    rank: 0, site_code: code, store_name: name, store_type: type, region_code: '', city,
    sales: cr(s), ly_sales: cr(ly), yoy_pct: pct(s, ly),
  };
  const perf = (p: Period) => {
    const r = scaleRow(base, p);
    return { sales: r.sales, ly_sales: r.ly_sales, yoy_pct: r.yoy_pct };
  };
  const isNew = type === 'NEW_STORE';
  return {
    site_code: code,
    store_name: name,
    city,
    state,
    store_type: type,
    is_lfl: isNew ? false : true,
    opening_date: isNew ? '2026-06-12' : '2019-03-01',
    age_months: isNew ? 4 : 90,
    performance: { today: perf('today'), yesterday: perf('yesterday'), wtd: perf('wtd'), mtd: perf('mtd') },
    lfl_growth: isNew ? null : { yoy_pct: pct(s * 0.9, ly * 0.8), sales: cr(s * 0.9), ly_sales: cr(ly * 0.8), period: 'mtd' },
  };
}
